'use client'

import { Check, Minus } from 'lucide-react'
import { REGEXP_ONLY_DIGITS } from 'input-otp'

import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import Input from '../elements/Input'
import { Button } from '../elements/Button'
import { useT } from '../i18n/LocaleProvider'
import type { MessageKey } from '@/lib/i18n/translate'
import { isValidEmail } from '@/lib/util'

/** Where a code goes: exactly one of the two, as `/auth/start` and the `otp` sign-in take it. */
export type Contact = { email: string, phone?: never, username?: never } | { phone: string, email?: never, username?: never }

/**
 * Whether the email-or-phone field is being typed as a number: nothing but
 * digits and the punctuation people put in phone numbers. Read on every
 * keystroke, before the number is complete, so the screen can change shape
 * (no password, an SMS hint) as soon as the first digits go in.
 */
export function looksLikePhone(value: string): boolean {
  return /^[+\d\s()-]+$/.test(value.trim()) && /\d/.test(value)
}

/**
 * Reads the field that takes either an email address or a phone number.
 *
 * A phone is eight digits once spaces, dashes and a +976 prefix are dropped,
 * so a number pasted from a contact card works as typed. Anything else with
 * an @ is held to the email rule. Null when it is neither yet.
 */
export function readContact(value: string): Contact | null {
  const text = value.trim()
  if(!text) return null
  if(looksLikePhone(text)) {
    const digits = text.replace(/\D/g, '').replace(/^976(?=\d{8}$)/, '')
    return /^\d{8}$/.test(digits) ? { phone: digits } : null
  }
  return isValidEmail(text) ? { email: text } : null
}

/**
 * The backend's error code from a rejected `signIn()`, or `fallback` when
 * there is none. Auth.js reports every credentials refusal as the error
 * "CredentialsSignin" and carries the reason in `code`, which is the generic
 * "credentials" unless auth.ts set one.
 */
export function signInErrorCode(response: { error?: string | null, code?: string | null }, fallback: string): string {
  if(response.code && response.code !== 'credentials') return response.code
  return !response.error || response.error === 'CredentialsSignin' ? fallback : response.error
}

/** Who a password sign-in names: a contact, or the account's handle. */
export type LoginId = Contact | { username: string, email?: never, phone?: never }

/**
 * Reads the sign-in field, which also takes a handle. In this order, because
 * a handle may be all digits and the phone has to win: a leading @ is a
 * handle; eight digits are a phone; anything else with an @ is an email;
 * whatever is left is a handle if it could be one. Null when it is none.
 */
export function readLogin(value: string): LoginId | null {
  const text = value.trim()
  if(text.startsWith('@')) {
    const handle = text.slice(1).toLowerCase()
    return isValidHandle(handle) ? { username: handle } : null
  }
  const contact = readContact(text)
  if(contact) return contact
  if(text.includes('@')) return null
  const handle = text.toLowerCase()
  return isValidHandle(handle) ? { username: handle } : null
}

/** A number as people write it back: 9911 2233. */
export function formatPhone(phone: string): string {
  return phone.replace(/^(\d{4})(\d{4})$/, '$1 $2')
}

/**
 * `components/ui/input-otp` is untyped JS, so its props do not survive into
 * TypeScript. Widening the three components once here beats scattering a
 * `@ts-expect-error` above every line of the markup below.
 */
const OTPRoot = InputOTP as any
const OTPGroup = InputOTPGroup as any
const OTPSlot = InputOTPSlot as any

/**
 * The six-digit code field.
 *
 * Wraps the shared `InputOTP` rather than re-implementing it — that component
 * already handles paste, arrow keys and mobile autofill. The slots are pulled
 * apart into separate cards here, which the base component draws as one joined
 * strip.
 *
 * `showChar` is on: this is a code from the reader's own inbox, not a secret
 * being typed in public, and masking it only makes a mistyped digit harder to
 * spot.
 */
export function OtpField({ value, onChange, onComplete, autoFocus = true, label }: {
  value: string
  onChange: (value: string) => void
  /** Fired once six digits are in, so the common case needs no button press. */
  onComplete?: (value: string) => void
  autoFocus?: boolean
  label: string
}) {
  const handleChange = (next: string) => {
    onChange(next)
    if(next.length === 6) onComplete?.(next)
  }

  return (
    <OTPRoot
      value={value}
      onChange={handleChange}
      maxLength={6}
      pattern={REGEXP_ONLY_DIGITS}
      autoFocus={autoFocus}
      autoComplete="one-time-code"
      aria-label={label}
      containerClassName="justify-center gap-2 lg:justify-start"
    >
      <OTPGroup className="gap-2">
        { [0, 1, 2, 3, 4, 5].map(index => (
          <OTPSlot
            key={index}
            index={index}
            showChar
            className="size-13 rounded-xl border border-input bg-card font-gilroy text-xl font-semibold shadow-xs first:rounded-l-xl last:rounded-r-xl sm:h-14 sm:w-13.5"
          />
        )) }
      </OTPGroup>
    </OTPRoot>
  )
}

/**
 * The three password rules, ticked off as they are met.
 *
 * Mirrors `passwordCriteria()` in the backend exactly — same three ids, same
 * order. Anything else and the checklist would say a password is fine that the
 * server then refuses, which is worse than showing no checklist at all.
 */
export const PASSWORD_RULES = [
  { id: 'length', test: (value: string) => value.length >= 8 },
  { id: 'letter', test: (value: string) => /\p{L}/u.test(value) },
  { id: 'number', test: (value: string) => /\d/.test(value) },
] as const

export function isPasswordStrong(password: string): boolean {
  return PASSWORD_RULES.every(rule => rule.test(password))
}

/**
 * @param compact renders only the unmet rules, for screens where the checklist
 *   is a correction rather than a running tally.
 */
export function PasswordRules({ password, compact = false }: { password: string, compact?: boolean }) {
  const t = useT()
  const rules = PASSWORD_RULES.map(rule => ({ id: rule.id, met: rule.test(password) }))
  const shown = compact ? rules.filter(rule => !rule.met) : rules

  if(!shown.length) return null

  return (
    <div className={compact ? 'flex flex-col gap-2' : 'grid gap-y-2 gap-x-3.5 sm:grid-cols-2'}>
      { shown.map(({ id, met }) => (
        <div key={id} className="flex items-center gap-2">
          <span
            className={`flex size-4.5 shrink-0 items-center justify-center rounded-full transition-colors ${
              met ? 'bg-success-foreground text-success' : 'bg-muted text-muted-foreground'
            }`}
            aria-hidden="true"
          >
            { met ? <Check className="size-3" /> : <Minus className="size-3" /> }
          </span>
          <span className={`text-xs font-medium leading-snug ${met ? 'text-foreground' : 'text-muted-foreground'}`}>
            {t(`auth.passwordRules.${id}` as MessageKey)}
          </span>
        </div>
      )) }
    </div>
  )
}

/**
 * New password plus its confirmation, with the checklist between them.
 *
 * Used by all three screens that set a password — the reset flow, the emailed
 * reset link, and step 3 of sign-up — because the rules and the mismatch
 * wording have to read the same in each. Whether the pair is usable is derived
 * here and handed back through `onValidChange`, so no caller re-implements it.
 *
 * @param compact passes through to the checklist: show only unmet rules.
 */
export function PasswordPair({ password, confirm, onPasswordChange, onConfirmChange, onSubmit, compact = false, autoFocus = false }: {
  password: string
  confirm: string
  onPasswordChange: (value: string) => void
  onConfirmChange: (value: string) => void
  /** Enter in either field submits, matching the single-field screens. */
  onSubmit?: () => void
  compact?: boolean
  autoFocus?: boolean
}) {
  const t = useT()
  const mismatch = !!confirm && confirm !== password
  const matched = !!confirm && confirm === password

  const handleKeyDown = (event: any) => {
    if(event.key === 'Enter') onSubmit?.()
  }

  return (
    <>
      <Input
        value={password}
        label={t('auth.reset.newPassword')}
        type="password"
        placeholder={t('auth.reset.newPasswordPlaceholder')}
        onChange={(event: any) => onPasswordChange(event.target.value)}
        onKeyDown={handleKeyDown}
        required
        autoFocus={autoFocus}
        container="!max-w-none"
      />

      { !!password && <PasswordRules password={password} compact={compact} /> }

      <Input
        value={confirm}
        label={t('auth.reset.repeatPassword')}
        type="password"
        placeholder={t('auth.reset.repeatPasswordPlaceholder')}
        onChange={(event: any) => onConfirmChange(event.target.value)}
        onKeyDown={handleKeyDown}
        errorText={mismatch ? t('auth.toast.passwordMismatch') : ''}
        description={matched ? t('auth.reset.matched') : ''}
        required
        container="!max-w-none"
      />
    </>
  )
}

/** Whether a new-password pair is ready to submit. */
export function isPasswordPairReady(password: string, confirm: string): boolean {
  return isPasswordStrong(password) && !!confirm && confirm === password
}

/**
 * Handle rules, mirroring `usernameRegex` and `validUsername` in the backend.
 * Keep the two in step — this side only decides what can be typed, the server
 * decides what can be claimed, and a gap between them shows up as a handle the
 * field accepts and the button then rejects.
 *
 * Note this is *not* `isValidUsername` from lib/util — that one allows uppercase
 * and no hyphen, neither of which matches the backend.
 *
 * Dots, hyphens and underscores are allowed inside, never at either end, because
 * a handle is the whole of `fuse.mn/{handle}` and a trailing separator reads as
 * a typo in a link someone else has to retype.
 */
export const HANDLE_MIN = 3
export const HANDLE_MAX = 30
const HANDLE_RE = /^[a-z0-9][a-z0-9._-]{1,28}[a-z0-9]$/

/** Strips what the backend would reject, so the field cannot hold an invalid handle. */
export function normaliseHandle(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, HANDLE_MAX)
}

export function isValidHandle(value: string): boolean {
  return value.length >= HANDLE_MIN && value.length <= HANDLE_MAX && HANDLE_RE.test(value)
}

/**
 * Sign in with Google.
 *
 * The four-colour mark stays an inline SVG on purpose — see CLAUDE.md. The flat
 * single-path `SiGoogle` glyph from Simple Icons is not Google's brand asset and
 * is not permitted in a sign-in button by their guidelines.
 */
export function GoogleButton({ onClick, isLoading }: { onClick: () => void, isLoading?: boolean }) {
  const t = useT()
  return (
    <Button onClick={onClick} isLoading={isLoading} loadingMessage={t('auth.signingIn')} variant="secondary" width="full">
      <svg className="size-5" aria-hidden="true" viewBox="0 0 24 24">
        <path d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z" fill="#EA4335" />
        <path d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z" fill="#4285F4" />
        <path d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z" fill="#FBBC05" />
        <path d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z" fill="#34A853" />
      </svg>
      <span className="text-sm font-semibold">{t('auth.google')}</span>
    </Button>
  )
}
